class AnimatedEnterpriseAccountingV2 {
  constructor() {
    this.selectors = {
      module: '.gg-animated-enterprise-accounting-v2',
      tab: '.wp-block-statik-tab',
      tabSelected: 'wp-block-statik-tab--selected',
      tabsContainer: '.wp-block-statik-tabs',
      tabsInnerBlocks: '.wp-block-statik-tabs__inner-blocks',
      tabsNav: '.wp-block-statik-tabs__nav',
      tabsNavList: '.wp-block-statik-tabs__nav-list',
      tabsNavItem: '.wp-block-statik-tabs__nav-item',
      tabsNavItemTitle: '.wp-block-statik-tabs__nav-item-title',
      tabsNavItemTitleColor: 'wp-block-statik-tabs__nav-item-title-color',
      tabsNavListItem: '.wp-block-statik-tabs__nav-list-item',
      tabsNavListItemSelected: 'wp-block-statik-tabs__nav-list-item--selected',
      tabsPanel: '.wp-block-statik-tabs__panel',
      itemsCircle: '.gg-animated-enterprise-accounting__item-circle',
      itemsCircleV2: '.gg-animated-enterprise-accounting__item-circle-v2',
      dropdownButton: '.dropdown-tab-button',
      dropdownButtonShow: 'dropdown-tab-button-show',
      dropdownButtonTitleColor: 'dropdown-tab-button-title-color',
      dropdownContent: '.gg-animated-enterprise-accounting__item',
      buttonLink: '.wp-block-button__link',
      scrollButtonForward: 'show-scroll-button',
      scrollButtonBack: 'show-scroll-button-back',
      lazyloadImage: 'img.lazyload'
    };
    
    this.config = {
      breakpoint: 640,
      animationSpeed: 400,
      smallScreenBehavior: {
        showFirstItemOnLoad: true,
        alwaysShowOneItem: true
      }
    };
    
    this.init();
  }
  
  init() {
    this.modules = document.querySelectorAll(this.selectors.module);
    this.modules.forEach(module => this.setupModule(module));
    this.initializeResponsiveDropdowns();
    this.initializeEarlyImageLoading();
  }
  
  setFixedHeight(module) {
    const tabPanels = module.querySelectorAll(this.selectors.tab);
    const tabsContainer = module.querySelector(this.selectors.tabsContainer);
    const mainContainer = module.closest(this.selectors.module);
    if (!tabPanels.length || !tabsContainer) return;

    let maxHeight = 0;
    const originalStyles = Array.from(tabPanels).map(panel => ({
      position: panel.style.position,
      visibility: panel.style.visibility,
      opacity: panel.style.opacity,
      display: panel.style.display,
      height: panel.style.height
    }));

    tabPanels.forEach(panel => {
      Object.assign(panel.style, {
        position: 'static',
        visibility: 'hidden',
        opacity: '0',
        display: 'block',
        height: 'auto'
      });
    });

    tabPanels.forEach(panel => {
      maxHeight = Math.max(maxHeight, panel.offsetHeight);
    });

    tabPanels.forEach((panel, index) => {
      Object.entries(originalStyles[index]).forEach(([prop, value]) => {
        panel.style[prop] = value;
      });
    });

    const innerBlocks = module.querySelector(this.selectors.tabsInnerBlocks);
    if (innerBlocks) {
      maxHeight += 20;
      innerBlocks.style.minHeight = `${maxHeight}px`;
      innerBlocks.style.height = `${maxHeight}px`;
    }

    if (mainContainer) {
      const tabNav = module.querySelector(this.selectors.tabsNav);
      const navHeight = tabNav?.offsetHeight || 0;
      const totalHeight = navHeight + maxHeight + 60;
      mainContainer.style.minHeight = `${totalHeight}px`;
    }
  }

  createScrollButton(direction) {
    const div = document.createElement('div');
    div.classList.add(direction === 'forward' ? this.selectors.scrollButtonForward : this.selectors.scrollButtonBack);
    div.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="20" viewBox="0 0 13 20" fill="none">
      <path d="M0.650023 2.36667L8.28336 10L0.650024 17.65L3.00002 20L13 10L3.00002 -2.05444e-07L0.650023 2.36667Z" fill="#0E2947"/>
    </svg>`;
    return div;
  }

  checkNavListOverflow(module) {
    const navList = module.querySelector(this.selectors.tabsNavList);
    const tabsNav = module.querySelector(this.selectors.tabsNav);
    if (!navList || !tabsNav || window.innerWidth < this.config.breakpoint) return;

    const existingButtons = tabsNav.querySelectorAll(`div.${this.selectors.scrollButtonForward}, div.${this.selectors.scrollButtonBack}`);
    existingButtons.forEach(btn => btn.remove());

    if (navList.scrollWidth > navList.clientWidth) {
      const forwardButton = this.createScrollButton('forward');
      const backButton = this.createScrollButton('back');
      tabsNav.append(forwardButton, backButton);

      const updateButtonVisibility = () => {
        const isAtStart = navList.scrollLeft <= 10;
        const isAtEnd = Math.ceil(navList.scrollLeft + navList.clientWidth) >= navList.scrollWidth - 10;

        backButton.style.opacity = isAtStart ? '0' : '1';
        backButton.style.pointerEvents = isAtStart ? 'none' : 'auto';
        forwardButton.style.opacity = isAtEnd ? '0' : '1';
        forwardButton.style.pointerEvents = isAtEnd ? 'none' : 'auto';
      };

      const handleScroll = (direction) => {
        const scrollAmount = direction === 'forward' ? 200 : -200;
        const maxScroll = navList.scrollWidth - navList.clientWidth;
        
        const remainingToEnd = maxScroll - navList.scrollLeft;
        const remainingToStart = navList.scrollLeft;
        const nextScrollPosition = navList.scrollLeft + scrollAmount;
        
        if (direction === 'forward') {
          if (nextScrollPosition > maxScroll || (remainingToEnd > 0 && remainingToEnd <= 300)) {
            navList.scrollTo({
              left: maxScroll,
              behavior: 'smooth'
            });
          } else {
            navList.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        } else {
          if (nextScrollPosition < 0 || (remainingToStart > 0 && remainingToStart <= 300)) {
            navList.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          } else {
            navList.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        }
        
        setTimeout(updateButtonVisibility, 500);
      };

      forwardButton.addEventListener('click', () => handleScroll('forward'));
      backButton.addEventListener('click', () => handleScroll('back'));
      navList.addEventListener('scroll', updateButtonVisibility);
      updateButtonVisibility();
    }
  }

  handleSelectedTabSync(module) {
    const navItems = [...module.querySelectorAll(this.selectors.tabsNavListItem)];
    const tabPanels = [...module.querySelectorAll(this.selectors.tab)];
    
    [...navItems, ...tabPanels].forEach(el => 
      el.classList.remove(this.selectors.tabsNavListItemSelected, this.selectors.tabSelected)
    );

    const selectedIndex = Math.max(
      navItems.findIndex(item => item.querySelector(`${this.selectors.tabsNavItem}[aria-selected="true"]`)),
      0
    );

    navItems[selectedIndex]?.classList.add(this.selectors.tabsNavListItemSelected);
    tabPanels[selectedIndex]?.classList.add(this.selectors.tabSelected);
  }

  syncTabContentWithDropdown(module) {
    const tabListItems = module.querySelectorAll(this.selectors.tabsNavListItem);
    const dropdownButtons = module.querySelectorAll(`${this.selectors.dropdownButton} ${this.selectors.buttonLink}`);
    if (!dropdownButtons.length) return;

    const syncContentByIndex = index => {
      const selectedTabItem = tabListItems[index];
      const titleContent = selectedTabItem?.querySelector(this.selectors.tabsNavItemTitle)?.innerHTML;
      if (titleContent && dropdownButtons[index]) {
        dropdownButtons[index].innerHTML = titleContent;
      }
    };

    tabListItems.forEach((tabItem, index) => {
      const tabButton = tabItem.querySelector(this.selectors.tabsNavItem);
      tabButton?.addEventListener('click', () => syncContentByIndex(index));
      syncContentByIndex(index);
    });
  }

  processTitleText(titleElement) {
    const titleText = titleElement.textContent;
    const [beforeDash, afterDash] = titleText.split(/ - | – /);
    if (beforeDash && afterDash) {
      titleElement.innerHTML = `<span class="${this.selectors.tabsNavItemTitleColor}">${beforeDash}</span>${titleText.includes(' - ') ? ' - ' : ' – '}${afterDash}`;
    }
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  }

  setupModule(module) {
    const tabTitles = module.querySelectorAll(this.selectors.tabsNavItemTitle);
    const itemsCircle = module.querySelector(this.selectors.itemsCircle);
    const itemsCircleV2 = module.querySelector(this.selectors.itemsCircleV2);
    const tabNavItems = module.querySelectorAll(this.selectors.tabsNavItem);
    const tabPanels = module.querySelectorAll(this.selectors.tabsPanel);
    let currentTabIndex = 0;
    let previousTabIndex = 0;

    window.addEventListener('resize', this.debounce(() => {
      this.checkNavListOverflow(module);
      this.setFixedHeight(module);
    }, 250));

    tabTitles.forEach(title => this.processTitleText(title));

    this.checkNavListOverflow(module);
    setTimeout(() => {
      this.setFixedHeight(module);
      this.checkNavListOverflow(module);
    }, 500);
    
    const navList = module.querySelector(this.selectors.tabsNavList);
    if (navList) {
      const observer = new MutationObserver(() => {
        this.checkNavListOverflow(module);
      });
      observer.observe(navList, { 
        attributes: true, 
        childList: true, 
        subtree: true 
      });
    }

    if ((itemsCircle || itemsCircleV2) && tabNavItems.length) {
      const initialActiveTab = Array.from(tabNavItems).findIndex(item => 
        item.classList.contains('is-active') || item.getAttribute('aria-selected') === 'true'
      );
      currentTabIndex = Math.max(initialActiveTab, 0);

      const updateCircleRotation = (newIndex) => {
        if (newIndex === previousTabIndex) return;
        
        const isMovingForward = newIndex > previousTabIndex || 
                               (newIndex === 0 && previousTabIndex === tabNavItems.length - 1);
        
        const animationName = isMovingForward ? 
          `rotateAnimationDown${newIndex}` : 
          `rotateAnimationUp${newIndex}`;
        
        const animationNameV2 = isMovingForward ? 
          `rotateTranslateAnimationDown${newIndex}` : 
          `rotateTranslateAnimationUp${newIndex}`;

        if (itemsCircle) {
          itemsCircle.style.animation = `${animationName} 1s ease-in-out forwards`;
        }

        if (itemsCircleV2) {
          itemsCircleV2.style.animation = `${animationNameV2} 1s ease-in-out forwards`;
        }
          
        previousTabIndex = newIndex;
      };

      tabNavItems.forEach((navItem, index) => {
        navItem.addEventListener('click', () => {
          currentTabIndex = index;
          updateCircleRotation(currentTabIndex);
        });
      });

      tabPanels.forEach((panel, index) => {
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'aria-hidden' || mutation.attributeName === 'hidden')) {
              const isVisible = !(panel.getAttribute('aria-hidden') === 'true' || panel.hasAttribute('hidden'));
              if (isVisible) {
                currentTabIndex = index;
                updateCircleRotation(currentTabIndex);
                setTimeout(() => this.setFixedHeight(module), 50);
              }
            }
          });
        });
        
        observer.observe(panel, { attributes: true });
      });

      updateCircleRotation(currentTabIndex);
    }

    this.syncTabContentWithDropdown(module);
    this.handleSelectedTabSync(module);

    module.querySelectorAll(this.selectors.tabsNavListItem).forEach(navItem => {
      navItem.addEventListener('click', () => setTimeout(() => this.handleSelectedTabSync(module), 0));
    });
  }

  slideDown(element, duration) {
    Object.assign(element.style, {
      display: 'flex',
      opacity: '0',
      overflow: 'hidden',
      maxHeight: '0px',
      transition: `max-height ${duration}ms ease, opacity ${duration}ms ease`
    });
    
    element.offsetHeight;
    element.style.maxHeight = `${element.scrollHeight}px`;
    element.style.opacity = '1';
    
    setTimeout(() => {
      Object.assign(element.style, {
        maxHeight: '',
        overflow: '',
        transition: ''
      });
    }, duration);
  }

  slideUp(element, duration) {
    Object.assign(element.style, {
      overflow: 'hidden',
      maxHeight: `${element.scrollHeight}px`,
      opacity: '1',
      transition: `max-height ${duration}ms ease, opacity ${duration}ms ease`
    });
    
    element.offsetHeight;
    Object.assign(element.style, {
      maxHeight: '0px',
      opacity: '0'
    });

    setTimeout(() => {
      Object.assign(element.style, {
        display: 'none',
        maxHeight: '',
        overflow: '',
        transition: '',
        opacity: ''
      });
    }, duration);
  }

  initializeResponsiveDropdowns() {
    document.querySelectorAll(this.selectors.module).forEach(module => {
      const dropdownButtons = module.querySelectorAll(this.selectors.dropdownButton);
      const dropdownContents = module.querySelectorAll(this.selectors.dropdownContent);
      
      if (!dropdownButtons.length || !dropdownContents.length) return;
      
      this.setupResponsiveDropdown(module, dropdownButtons, dropdownContents);
    });
  }

  setupResponsiveDropdown(module, dropdownButtons, dropdownContents) {
    const processDropdownButtonText = button => {
      const anchorElement = button.querySelector('a');
      if (!anchorElement) return;
      
      const titleText = anchorElement.textContent.trim();
      const [beforeDash, afterDash] = titleText.split(/ - | – /);
      
      if (beforeDash && afterDash) {
        anchorElement.innerHTML = `<span class="${this.selectors.dropdownButtonTitleColor}">${beforeDash}</span>${titleText.includes(' - ') ? ' - ' : ' – '}${afterDash}`;
      }
    };
    
    const updateDropdownButtonsState = () => {
      dropdownButtons.forEach(button => {
        const parentTab = button.closest(this.selectors.tab);
        if (parentTab && parentTab.classList.contains(this.selectors.tabSelected)) {
          button.classList.add(this.selectors.dropdownButtonShow);
        } else {
          button.classList.remove(this.selectors.dropdownButtonShow);
        }
      });
    };

    const handleScreenSize = () => {
      const selectedTab = module.querySelector(`.${this.selectors.tabSelected}`);
      const isDesktop = window.innerWidth >= this.config.breakpoint;

      dropdownContents.forEach(content => {
        content.style.display = isDesktop ? '' : 'none';
      });

      if (!isDesktop) {
        const selectedContent = selectedTab?.querySelector(this.selectors.dropdownContent);
        if (selectedContent) {
          selectedContent.style.display = 'flex';
        } else if (this.config.smallScreenBehavior.showFirstItemOnLoad && dropdownContents.length > 0) {
          dropdownContents[0].style.display = 'flex';
        }
        updateDropdownButtonsState();
      }
    };

    const handleDropdownButtonClick = () => {
      let isAnimating = false;

      if (this.config.smallScreenBehavior.showFirstItemOnLoad && dropdownButtons.length > 0) {
        const firstButton = dropdownButtons[0];
        const firstContent = firstButton.closest(this.selectors.tab)
          .querySelector(this.selectors.dropdownContent);
        
        if (getComputedStyle(firstContent).display !== 'none') {
          firstButton.classList.add(this.selectors.dropdownButtonShow);
          firstButton.closest(this.selectors.tab).classList.add(this.selectors.tabSelected);
        }
      }

      dropdownButtons.forEach(button => {
        button.addEventListener('click', e => {
          e.preventDefault();
          if (isAnimating || window.innerWidth >= this.config.breakpoint) return;
          
          const parentModule = button.closest(this.selectors.tab);
          const mainModule = button.closest(this.selectors.module);
          const relatedContent = parentModule.querySelector(this.selectors.dropdownContent);
          const itemsCircle = document.querySelector(this.selectors.itemsCircleV2);
          const visibleContent = Array.from(dropdownContents).find(content => 
            getComputedStyle(content).display !== 'none'
          );

          isAnimating = true;

          const switchTabs = () => {
            dropdownButtons.forEach(btn => 
              btn !== button && btn.classList.remove(this.selectors.dropdownButtonShow)
            );
            
            dropdownContents.forEach(content => {
              if (content !== relatedContent) {
                content.closest(this.selectors.tab).classList.remove(this.selectors.tabSelected);
              }
            });

            button.classList.add(this.selectors.dropdownButtonShow);
            parentModule.classList.add(this.selectors.tabSelected);

            if (itemsCircle) {
              const rotation = Array.from(parentModule.parentElement.children)
                .indexOf(parentModule) * 180;
              itemsCircle.style.transform = `translateX(-50%) rotate(${rotation}deg)`;
            }

            const navItems = mainModule.querySelectorAll(this.selectors.tabsNavListItem);
            const currentIndex = Array.from(parentModule.parentElement.children)
              .indexOf(parentModule);
            
            navItems.forEach(item => 
              item.classList.remove(this.selectors.tabsNavListItemSelected)
            );
            navItems[currentIndex]?.classList.add(this.selectors.tabsNavListItemSelected);
            
            updateDropdownButtonsState();
          };

          if (mainModule) {
            const moduleOffset = mainModule.getBoundingClientRect().top + window.pageYOffset + 150;
            window.scrollTo({ top: moduleOffset, behavior: 'smooth' });
          }

          if (visibleContent && visibleContent !== relatedContent) {
            this.slideUp(visibleContent, this.config.animationSpeed);
            setTimeout(() => {
              switchTabs();
              this.slideDown(relatedContent, this.config.animationSpeed);
              setTimeout(() => isAnimating = false, this.config.animationSpeed);
            }, this.config.animationSpeed);
          } else {
            switchTabs();
            this.slideDown(relatedContent, this.config.animationSpeed);
            setTimeout(() => isAnimating = false, this.config.animationSpeed);
          }
        });
      });
    };

    dropdownButtons.forEach(button => processDropdownButtonText(button));
    handleScreenSize();
    handleDropdownButtonClick();
    window.addEventListener('resize', handleScreenSize);

    updateDropdownButtonsState();

    const tabsObserver = new MutationObserver(() => {
      updateDropdownButtonsState();
    });
    
    module.querySelectorAll(this.selectors.tab).forEach(tab => {
      tabsObserver.observe(tab, { attributes: true, attributeFilter: ['class'] });
    });
  }

  initializeEarlyImageLoading() {
    if (window.innerWidth >= this.config.breakpoint) return;

    let hasTriggeredEarlyLoad = false;

    const removeLazyLoading = () => {
      hasTriggeredEarlyLoad = true;
      
      document.querySelectorAll(`${this.selectors.tab} ${this.selectors.lazyloadImage}`).forEach(img => {
        img.removeAttribute('loading');
        img.classList.remove('lazyload');
        
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        if (img.dataset.sizes) {
          img.sizes = img.dataset.sizes;
          img.removeAttribute('data-sizes');
        }
        if (img.src && img.src.startsWith('data:image/gif;base64')) {
          img.removeAttribute('src');
        }
      });
    };

    const isInViewport = element => {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    };

    document.querySelectorAll(this.selectors.module).forEach(module => {
      if (isInViewport(module)) {
        removeLazyLoading();
        return;
      }
    });

    if (!hasTriggeredEarlyLoad) {
      const handleScroll = () => {
        if (hasTriggeredEarlyLoad) return;
        
        if (window.scrollY > 10) {
          removeLazyLoading();
          window.removeEventListener('scroll', handleScroll);
        }
      };

      window.addEventListener('scroll', handleScroll);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AnimatedEnterpriseAccountingV2();
});

